import { useRef, useState } from "react";
import { api, fileUrl } from "../lib/api";
import { scanClip } from "../lib/clipScan";
import { parseTimestamp, SCAN_PRESETS } from "../lib/constants";
import { ClipPlayer } from "./ClipPlayer";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Clock, Eye, Sparkles, ScanSearch, Loader2, Wand2, Pin, Check, ExternalLink, Zap, FlaskConical, Film, Volume2, VolumeX, ListVideo, ChevronDown, ChevronUp, Target, ShieldAlert } from "lucide-react";

// One clip on a case: embedded player + click-to-seek tag + AI shot log + (mod-only) scan/tag/mute.
// Start the clip a few seconds early so the dribble/wind-up into the green is visible.
const LEAD_IN_SECONDS = 3;

const ShotBadges = ({ r }) => (
  <span className="flex shrink-0 items-center gap-1">
    {r.green && <span title="Perfect / green release" className="inline-flex items-center gap-0.5 rounded bg-[#00FF87]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[#00FF87]"><Check size={9} /> Green</span>}
    {r.contested && <span title="Contested at release" className="inline-flex items-center gap-0.5 rounded bg-[#FF2A55]/15 px-1 py-0.5 text-[9px] font-bold uppercase text-[#FF6B8A]"><ShieldAlert size={9} /> Contested</span>}
    {r.highlight && <span title="Matches your search" className="inline-flex items-center gap-0.5 rounded bg-[#FFB800]/20 px-1 py-0.5 text-[9px] font-bold uppercase text-[#FFB800]"><Target size={9} /> Match</span>}
  </span>
);

export const ClipEvidence = ({ reportId, url, evidence, isMod, gamertag = "", index = 0, scanPending = false, isDemo = false, onSaved }) => {
  const playerRef = useRef(null);
  const playerWrapRef = useRef(null);
  const [prompt, setPrompt] = useState("");
  const [scanning, setScanning] = useState(false);
  const [results, setResults] = useState(null);
  const [engine, setEngine] = useState(null);
  const [note, setNote] = useState(null);
  const [savingTs, setSavingTs] = useState(null);
  const [savingMute, setSavingMute] = useState(false);
  const [showTools, setShowTools] = useState(false);
  const [showLog, setShowLog] = useState(true);
  const [manualTs, setManualTs] = useState(evidence?.timestamp || "");
  const [manualNote, setManualNote] = useState(evidence?.note || "");

  const ts = evidence?.timestamp || "";
  const tagNote = evidence?.note || "";
  const muted = !!evidence?.muted;
  const shots = evidence?.ai_shots || [];
  const shotTotal = shots.length;
  const shotMade = shots.filter((s) => s.made || s.green).length;
  const shotGreens = shots.filter((s) => s.green).length;
  const shotCG = shots.filter((s) => s.green && s.contested).length;
  const madePct = shotTotal ? Math.round((shotMade / shotTotal) * 100) : 0;
  const playUrl = url && url.startsWith("/api/files/") ? fileUrl(url) : url;
  const canPlay = !isDemo && ClipPlayer && playUrl;

  const seekTo = (seconds) => {
    // Bring the player into view first so you never have to scroll back up, then play from the build-up.
    if (playerWrapRef.current) playerWrapRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    if (playerRef.current) playerRef.current.seekTo(Math.max(0, seconds - LEAD_IN_SECONDS));
  };

  const runScan = async () => {
    setResults(null); setEngine(null); setNote(null); setScanning(true);
    try {
      const data = await scanClip({ url, prompt: prompt.trim(), player: gamertag });
      setResults(data.results || []); setEngine(data.engine); setNote(data.note || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Scan failed.");
    } finally { setScanning(false); }
  };

  const saveTag = async (timestamp, noteText) => {
    setSavingTs(timestamp);
    try {
      const { data } = await api.patch(`/reports/${reportId}/evidence`, { url, timestamp, note: noteText });
      toast.success(`Tagged this clip → jump to ${timestamp || "start"}`);
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not tag the clip.");
    } finally { setSavingTs(null); }
  };

  const toggleMute = async () => {
    setSavingMute(true);
    try {
      const { data } = await api.patch(`/reports/${reportId}/evidence`, { url, muted: !muted });
      toast.success(!muted ? "Clip set to play muted." : "Clip sound turned back on.");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update the clip audio.");
    } finally { setSavingMute(false); }
  };

  const pickPreset = (p) => setPrompt(p === "All jump shots" ? "" : p);

  return (
    <div className="rounded-lg border border-[#1E293F] bg-[#141B2D]" data-testid={`clip-evidence-${index}`}>
      {canPlay && (
        <div ref={playerWrapRef} className="sticky top-[46px] z-10 overflow-hidden rounded-t-lg bg-black" data-testid={`clip-player-wrap-${index}`}>
          <ClipPlayer ref={playerRef} url={playUrl} muted={muted} testid={`clip-player-${index}`} />
        </div>
      )}
      {isDemo && (
        <div className="flex items-center justify-center gap-2 rounded-t-lg border-b border-[#1E293F] bg-[#0B1120] py-8 text-[#475569]" style={{ aspectRatio: "16 / 9" }} data-testid={`clip-demo-placeholder-${index}`}>
          <Film size={22} /> <span className="text-sm font-semibold uppercase tracking-wider">Evidence Clip</span>
        </div>
      )}

      <div className="p-3">
        <div className="flex items-center gap-2">
          {isDemo ? (
            <>
              <a href={url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-[#00FF87] hover:underline" data-testid={`clip-url-${index}`}>▶ Watch the clip</a>
              <ExternalLink size={13} className="shrink-0 text-[#64748B]" />
            </>
          ) : (
            <>
              <a href={playUrl} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm text-[#00FF87] hover:underline" data-testid={`clip-url-${index}`}>{url.startsWith("/api/files/") ? "Uploaded clip" : url}</a>
              <ExternalLink size={13} className="shrink-0 text-[#64748B]" />
              {muted && (
                <span title="Plays muted by default" className="inline-flex shrink-0 items-center gap-1 rounded bg-[#1E293F] px-1.5 py-0.5 text-[10px] font-bold uppercase text-[#94A3B8]" data-testid={`clip-muted-badge-${index}`}><VolumeX size={11} /> Muted</span>
              )}
              {isMod && canPlay && (
                <button onClick={toggleMute} disabled={savingMute} data-testid={`clip-mute-toggle-${index}`}
                  title={muted ? "Turn sound back on for viewers" : "Play this clip muted by default"}
                  className="inline-flex shrink-0 items-center gap-1 rounded border border-[#1E293F] bg-[#0F1424] px-1.5 py-1 text-[10px] font-semibold text-[#94A3B8] transition-colors hover:text-white">
                  {savingMute ? <Loader2 size={12} className="animate-spin" /> : (muted ? <Volume2 size={12} /> : <VolumeX size={12} />)}
                  {muted ? "Unmute" : "Mute"}
                </button>
              )}
            </>
          )}
        </div>

        {ts && (
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button onClick={() => seekTo(parseTimestamp(ts) || 0)} data-testid={`clip-jumpto-${index}`}
              className="inline-flex items-center gap-1 rounded bg-[#00F0FF]/15 px-2 py-1 text-xs font-bold text-[#00F0FF] transition-colors hover:bg-[#00F0FF]/30">
              <Clock size={12} /> Jump to {ts}
            </button>
            {evidence?.ai_suggested && (
              <span data-testid={`clip-ai-suggested-${index}`} title="Auto-suggested by AI — a mod can confirm or change it" className="inline-flex items-center gap-1 rounded-full border border-[#A855F7]/40 bg-[#A855F7]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#C084FC]">
                <Sparkles size={10} /> AI suggested
              </span>
            )}
          </div>
        )}
        {!ts && scanPending && (
          <div className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/[0.08] px-2.5 py-1 text-[11px] font-semibold text-[#C084FC]" data-testid={`clip-scan-pending-${index}`}>
            <Loader2 size={12} className="animate-spin" /> AI is analyzing this clip…
          </div>
        )}
        {tagNote && (
          <div className="mt-2 flex items-start gap-1.5 rounded border border-[#F97316]/20 bg-[#F97316]/[0.06] px-2.5 py-1.5 text-xs leading-snug text-[#FCD34D]" data-testid={`clip-note-${index}`}>
            <Eye size={13} className="mt-px shrink-0" /> <span><span className="font-semibold text-[#FDE68A]">Watch for:</span> {tagNote}</span>
          </div>
        )}

        {shots.length > 0 && (
          <div className="mt-3 rounded-lg border border-[#00FF87]/20 bg-[#00FF87]/[0.04] p-2.5" data-testid={`clip-shotlog-${index}`}>
            <button onClick={() => setShowLog(!showLog)} className="flex w-full items-center gap-2 text-left text-xs font-bold uppercase tracking-wider text-[#00FF87]" data-testid={`clip-shotlog-toggle-${index}`}>
              <ListVideo size={14} /> Every shot the AI found ({shots.length})
              <span className="ml-auto normal-case text-[#64748B]">{showLog ? <ChevronUp size={14} /> : <ChevronDown size={14} />}</span>
            </button>
            <div className="mt-2" data-testid={`clip-shotchart-${index}`}>
              <div className="flex flex-wrap items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider">
                <span className="rounded bg-[#0F1424] px-1.5 py-0.5 text-[#94A3B8]">FG {shotMade}/{shotTotal} ({madePct}%)</span>
                <span className="rounded bg-[#00FF87]/15 px-1.5 py-0.5 text-[#00FF87]">{shotGreens} green</span>
                <span className="rounded bg-[#FF2A55]/15 px-1.5 py-0.5 text-[#FF6B8A]">{shotCG} contested green</span>
              </div>
              <div className="mt-1.5 flex h-2 w-full overflow-hidden rounded-full bg-[#1E293F]" title={`${shotMade} made · ${shotTotal - shotMade} missed`}>
                <div className="h-full bg-[#00FF87]" style={{ width: `${madePct}%` }} />
                <div className="h-full bg-[#475569]" style={{ width: `${100 - madePct}%` }} />
              </div>
            </div>
            {showLog && (
              <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto pr-1">
                {shots.map((r, i) => (
                  <button key={i} onClick={() => seekTo(r.sec)} data-testid={`clip-shot-${index}-${i}`}
                    className="flex w-full items-center gap-2 rounded border border-[#1E293F] bg-[#0F1424] p-2 text-left transition-colors hover:border-[#00FF87]/40">
                    <span className="flex shrink-0 items-center gap-1 rounded bg-[#00F0FF]/15 px-1.5 py-0.5 font-mono text-xs font-bold text-[#00F0FF]"><Clock size={11} /> {r.ts}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-white">{r.label}</span>
                    <ShotBadges r={r} />
                    <span className="shrink-0 font-mono text-[10px] text-[#64748B]">{r.conf}%</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {isMod && (
          <div className="mt-3 rounded-lg border border-[#00F0FF]/20 bg-[#0B1B24] p-2.5" data-testid={`clip-mod-tools-${index}`}>
            <button onClick={() => setShowTools(!showTools)} className="flex w-full items-center gap-2 text-left text-xs font-bold uppercase tracking-wider text-[#00F0FF]" data-testid={`clip-tools-toggle-${index}`}>
              <Sparkles size={13} /> AI scan & tag this clip <span className="ml-auto text-[#64748B] normal-case">{showTools ? "hide" : "open"}</span>
            </button>

            {showTools && (
              <div className="mt-3">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Wand2 size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[#00F0FF]" />
                    <Input data-testid={`clip-scan-prompt-${index}`} value={prompt} onChange={(e) => setPrompt(e.target.value)}
                      placeholder="empty = every shot · or highlight e.g. half court" className="h-9 border-[#1E293F] bg-[#0F1424] pl-8 text-xs text-white placeholder:text-[#64748B]" />
                  </div>
                  <Button size="sm" data-testid={`clip-scan-btn-${index}`} onClick={runScan} disabled={scanning}
                    className="h-9 shrink-0 bg-[#00F0FF] text-xs font-bold text-[#04222A] hover:bg-[#00F0FF]/90">
                    {scanning ? <Loader2 size={13} className="mr-1 animate-spin" /> : <ScanSearch size={13} className="mr-1" />} Scan
                  </Button>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {SCAN_PRESETS.map((p) => (
                    <button key={p} data-testid={`clip-scan-preset-${index}-${p.replace(/[^a-z0-9]+/gi, "-")}`} onClick={() => pickPreset(p)}
                      className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${(prompt === p || (p === "All jump shots" && !prompt)) ? "border-[#00F0FF] bg-[#00F0FF]/15 text-[#00F0FF]" : "border-[#1E293F] bg-[#0F1424] text-[#94A3B8] hover:text-[#00F0FF]"}`}>
                      {p}
                    </button>
                  ))}
                </div>

                {results && !scanning && (
                  <div className="mt-2" data-testid={`clip-scan-results-${index}`}>
                    <div className="flex items-center gap-2 text-[11px] text-[#64748B]">
                      {engine === "gemini" ? <span className="inline-flex items-center gap-1 text-[#00FF87]"><Zap size={10} /> Live AI</span> : <span className="inline-flex items-center gap-1 text-[#FFB800]"><FlaskConical size={10} /> Sample</span>}
                      · {results.length} found · click ⏱ to preview, 📌 to pin
                    </div>
                    {note && <p className="mt-1 text-[11px] leading-snug text-[#FCD34D]">{note}</p>}
                    <div className="mt-2 max-h-64 space-y-1.5 overflow-y-auto pr-1">
                      {results.map((r, i) => (
                        <div key={i} className="flex items-center gap-2 rounded border border-[#1E293F] bg-[#0F1424] p-2" data-testid={`clip-scan-row-${index}-${i}`}>
                          {engine === "demo" ? (
                            <span title="Example only — this clip couldn't be scanned" className="flex shrink-0 items-center gap-1 rounded bg-[#334155]/40 px-1.5 py-0.5 font-mono text-xs font-bold text-[#64748B]">
                              <Clock size={11} /> {r.ts}
                            </span>
                          ) : (
                            <button onClick={() => seekTo(r.sec)} data-testid={`clip-scan-seek-${index}-${i}`}
                              className="flex shrink-0 items-center gap-1 rounded bg-[#00F0FF]/15 px-1.5 py-0.5 font-mono text-xs font-bold text-[#00F0FF] hover:bg-[#00F0FF]/30">
                              <Clock size={11} /> {r.ts}
                            </button>
                          )}
                          <span className={`min-w-0 flex-1 truncate text-xs ${engine === "demo" ? "text-[#64748B]" : "text-white"}`}>{r.label}</span>
                          {engine !== "demo" && <ShotBadges r={r} />}
                          <span className={`shrink-0 font-mono text-[10px] ${engine === "demo" ? "text-[#64748B]" : "text-[#00FF87]"}`}>{r.conf}%</span>
                          {engine !== "demo" && (
                            <Button size="sm" variant="outline" data-testid={`clip-scan-tag-${index}-${i}`} disabled={savingTs === r.ts}
                              onClick={() => saveTag(r.ts, r.label)}
                              className="h-7 shrink-0 border-[#00F0FF]/40 bg-[#00F0FF]/10 px-2 text-[11px] font-semibold text-[#00F0FF] hover:bg-[#00F0FF]/20 hover:text-[#00F0FF]">
                              {savingTs === r.ts ? <Loader2 size={12} className="animate-spin" /> : <><Pin size={11} className="mr-1" /> Pin</>}
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-3 border-t border-[#1E293F] pt-2.5">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">Or tag manually</p>
                  <div className="mt-1.5 grid grid-cols-[90px_1fr_auto] gap-1.5">
                    <Input data-testid={`clip-manual-ts-${index}`} value={manualTs} onChange={(e) => setManualTs(e.target.value)}
                      placeholder="0:34" className="h-8 border-[#1E293F] bg-[#0F1424] text-xs text-white placeholder:text-[#64748B]" />
                    <Input data-testid={`clip-manual-note-${index}`} value={manualNote} onChange={(e) => setManualNote(e.target.value)}
                      placeholder="what to look for" className="h-8 border-[#1E293F] bg-[#0F1424] text-xs text-white placeholder:text-[#64748B]" />
                    <Button size="sm" data-testid={`clip-manual-save-${index}`} disabled={savingTs === manualTs} onClick={() => saveTag(manualTs.trim(), manualNote.trim())}
                      className="h-8 bg-[#00FF87] px-2 text-[11px] font-bold text-[#062B1A] hover:bg-[#00FF87]/90">
                      {savingTs === manualTs ? <Loader2 size={12} className="animate-spin" /> : <Check size={13} />}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
